// CareLogix™ ORACLE — mode/pick/query configuration

export const AGENT_MODES = [
  {
    id: "stocks",
    label: "Stock Picks",
    short: "Stocks",
    tag: "Equity Intelligence",
    icon: "📈",
    accent: "#d4af37",
    blurb: "Public-market equity intelligence with verdict, entry, target, stop, catalysts, drivers, risks, confidence.",
  },
  {
    id: "crypto",
    label: "Crypto Plays",
    short: "Crypto",
    tag: "Digital Asset Edge",
    icon: "₿",
    accent: "#f7a800",
    blurb: "On-chain + tokenomics + macro liquidity reads across BTC, ETH, SOL and L2/AI sectors.",
  },
  {
    id: "oanda",
    label: "OANDA FX",
    short: "OANDA FX",
    tag: "FX Intelligence",
    icon: "🌐",
    accent: "#c8a64b",
    blurb: "Simulated OANDA-style PERCEIVE → PLAN → ACT → SYNTHESIZE workflow on FX, metals, and energy pairs.",
  },
  {
    id: "sports",
    label: "Sports Bets",
    short: "Sports",
    tag: "Wagering Intel (21+)",
    icon: "🏆",
    accent: "#e8c25c",
    blurb: "Lines, sharp vs public, ATS, units, fade factors. Educational research only.",
  },
  {
    id: "options",
    label: "Options Flow",
    short: "Options",
    tag: "Derivatives & IV",
    icon: "📡",
    accent: "#d4af37",
    blurb: "Unusual flow, dealer gamma, IV/HV skew, strike/expiry guidance for the next 1–4 weeks.",
  },
  {
    id: "swing",
    label: "Swing Trades",
    short: "Swing",
    tag: "Tactical Setups",
    icon: "⚡",
    accent: "#e6b84a",
    blurb: "Multi-day to multi-week swings with clear levels, invalidation triggers, and risk envelopes.",
  },
  {
    id: "catalyst",
    label: "Catalyst Plays",
    short: "Catalyst",
    tag: "Event-Driven",
    icon: "🔥",
    accent: "#d4af37",
    blurb: "Earnings, FDA, M&A, macro prints, product reveals — with date-anchored windows.",
  },
  {
    id: "retire",
    label: "Retirement",
    short: "Retirement",
    tag: "Wealth Planning",
    icon: "🏦",
    accent: "#e8a020",
    blurb: "Compound growth projections, nest-egg calculator, and tax-wrapper optimization powered by LOGIX-2™.",
  },
  {
    id: "esports",
    label: "Gaming & Esports",
    short: "Esports",
    tag: "Gaming Intel (21+)",
    icon: "🎮",
    accent: "#c860a8",
    blurb: "CS2, LoL, Valorant, Dota 2, Rocket League, COD — line analysis with map-pool and meta edge.",
  },
];

export const QUICK_PICKS = {
  stocks: [
    { label: "NVDA 2-week read", prompt: "Give me a 2-week ORACLE read on NVDA with current AI capex momentum and any upcoming catalysts." },
    { label: "AAPL setup", prompt: "AAPL setup for the next 3 weeks — services growth, China demand, and product cycle drivers." },
    { label: "AI leaders rotation", prompt: "Who are the strongest AI infrastructure leaders right now (semis + power + cloud)? Pick the top conviction play." },
    { label: "Russell 2000 small-caps", prompt: "Best small-cap (Russell 2000) swing candidates with quick-turnaround catalysts." },
  ],
  crypto: [
    { label: "BTC 2-week", prompt: "BTC next 2 weeks — macro, ETF flows, on-chain. Verdict + entry/target/stop." },
    { label: "ETH vs SOL", prompt: "ETH vs SOL relative-strength setup. Which has the cleaner 1-month edge?" },
    { label: "AI tokens", prompt: "Top AI-narrative crypto tokens with a catalyst window. Conviction picks only." },
    { label: "DeFi rotation", prompt: "Is DeFi rotation about to fire? Best ORACLE pick to express it." },
  ],
  oanda: [
    { label: "EUR/USD week", prompt: "EUR/USD next-week setup. ECB vs Fed divergence and DXY direction." },
    { label: "USD/JPY carry", prompt: "USD/JPY — carry trade risk, BoJ posture, intervention zones." },
    { label: "XAU/USD gold", prompt: "XAU/USD — real yields, central-bank gold buying, risk-off positioning." },
    { label: "GBP risk-on", prompt: "GBP/USD risk-on path with UK CPI and BoE pricing." },
  ],
  sports: [
    { label: "NBA tonight", prompt: "Strongest NBA edge tonight — give one ATS play with units and fade factors." },
    { label: "NFL Sunday", prompt: "NFL Sunday — sharpest single-game ORACLE pick with line analysis." },
    { label: "UFC main", prompt: "Upcoming UFC main event — ML or method-of-victory edge." },
    { label: "Soccer EPL", prompt: "EPL weekend — best value side or O/U with sharp-money read." },
  ],
  options: [
    { label: "SPY 0DTE flow", prompt: "Read today’s SPY 0DTE flow context. Best directional or vol play next 1–3 sessions." },
    { label: "NVDA call spreads", prompt: "NVDA call-spread structure for a 2-week move. Strike/expiry suggestions." },
    { label: "Unusual activity", prompt: "Where is the highest-conviction unusual options activity right now?" },
    { label: "Earnings IV crush", prompt: "Best earnings IV-crush short setups this week." },
  ],
  swing: [
    { label: "Top semi swing", prompt: "Top semiconductor swing setup for the next 2–3 weeks with invalidation triggers." },
    { label: "Energy swing", prompt: "Best energy swing right now — oil services or majors?" },
    { label: "Biotech catalyst swing", prompt: "A biotech swing with a near-term catalyst window." },
    { label: "Breakout watchlist", prompt: "Build a 5-name breakout watchlist for this week with triggers and stops." },
  ],
  catalyst: [
    { label: "This week’s macro", prompt: "Map this week’s macro catalysts (CPI/PPI/FOMC/jobs) and the cleanest trade expression." },
    { label: "Earnings event", prompt: "Highest-edge earnings event this week and how to play it." },
    { label: "FDA catalyst", prompt: "Next major FDA catalyst with a defined risk play." },
    { label: "M&A rumor", prompt: "Most credible M&A rumor with a tradable expression and risk envelope." },
  ],
  retire: [
    { label: "On track at 65?", prompt: "I'm 38 with $180K saved and contribute $1,200/mo. Will I retire comfortably at 65?" },
    { label: "Roth vs Traditional", prompt: "I earn $95K. Roth 401(k) vs Traditional 401(k) — which wins for me and why?" },
    { label: "$300K → 30 yrs @ 8%", prompt: "Project how $300K grows over 30 years at 8% with monthly $500 contributions." },
    { label: "Catch-up at 52", prompt: "I'm 52 and behind. What catch-up contribution strategy maximizes my next 13 years?" },
  ],
  esports: [
    { label: "CS2 best bet", prompt: "Best CS2 match bet today with map-pool edge and sharp-money read." },
    { label: "LoL Worlds outright", prompt: "LoL Worlds outright value — best contender vs market consensus." },
    { label: "Valorant VCT", prompt: "Valorant VCT — top underdog value pick this slate." },
    { label: "Dota 2 TI", prompt: "Dota 2 The International — best tournament-stage stat edge." },
  ],
};

export const SAMPLE_QUERIES = {
  stocks: [
    "What’s the highest-conviction megacap to own for the next 2 weeks and why?",
    "Walk me through a 1-month bullish setup on a quality compounder with a clean stop.",
    "Pick one underowned mid-cap with a 20%+ upside path and define the risk.",
  ],
  crypto: [
    "Is BTC range-bound or about to expand? Give one trade plan either way.",
    "Which sector inside crypto has the cleanest 30-day edge — L1s, L2s, DeFi, AI?",
    "Convert a macro liquidity thesis into a single crypto expression with entry/stop.",
  ],
  oanda: [
    "Give me the top FX pair with the cleanest week-ahead setup.",
    "Where is dollar liquidity skewed and which cross expresses that best?",
    "Build a risk-off basket idea using FX + gold + JPY.",
  ],
  sports: [
    "Sharpest single-game ATS pick tonight with units and fade factors.",
    "Best player-prop edge for the marquee game.",
    "Where is the public most wrong on this week’s slate?",
  ],
  options: [
    "Define a 2-week defined-risk options trade on the top conviction stock.",
    "Where is dealer gamma positioned right now — and how do we play it?",
    "Best calendar/diagonal idea heading into earnings.",
  ],
  swing: [
    "Give me a clean 3-week swing with a tight invalidation level.",
    "Which sector ETF has the best multi-week swing setup?",
    "One contrarian swing with a real margin of safety.",
  ],
  catalyst: [
    "Which catalyst this week has the most asymmetry?",
    "Build a calendar of the top 5 catalysts and the cleanest expression for each.",
    "Where is the market mispricing an upcoming event?",
  ],
  retire: [
    "I'm 38, $180K saved, $1,200/mo — am I on pace for retirement at 65?",
    "Walk me through Roth 401(k) vs Traditional 401(k) for an $95K earner.",
    "Show how a $500/mo contribution at 8% compounds over 30 years.",
  ],
  esports: [
    "Strongest CS2 match bet today with map-pool edge.",
    "Best LoL Worlds outright value vs market consensus.",
    "Sharpest Valorant VCT underdog play this week.",
  ],
};

export const OANDA_PAIRS = [
  { symbol: "EUR/USD", group: "Majors", emoji: "🇪🇺" },
  { symbol: "GBP/USD", group: "Majors", emoji: "🇬🇧" },
  { symbol: "USD/JPY", group: "Majors", emoji: "🇯🇵" },
  { symbol: "AUD/USD", group: "Majors", emoji: "🇦🇺" },
  { symbol: "USD/CAD", group: "Majors", emoji: "🇨🇦" },
  { symbol: "USD/CHF", group: "Majors", emoji: "🇨🇭" },
  { symbol: "XAU/USD", group: "Metals", emoji: "🥇" },
  { symbol: "XTI/USD", group: "Energy", emoji: "🛢️" },
  { symbol: "EUR/GBP", group: "Crosses", emoji: "🇪🇬" },
  { symbol: "CAD/JPY", group: "Crosses", emoji: "🇨🇯" },
];

export const OANDA_BADGES = [
  "OANDA Style · SIMULATED",
  "CFTC / NFA Spirit",
  "FCA-Style Risk Notices",
  "ASIC-Style Disclosures",
  "MiFID-Aware Workflow",
];

export const OANDA_WORKFLOW_STEPS = [
  { id: "perceive", label: "PERCEIVE", detail: "Ingest macro, central-bank, liquidity, sentiment" },
  { id: "plan", label: "PLAN", detail: "Build hypotheses, levels, risk envelope" },
  { id: "act", label: "ACT", detail: "Generate verdict, entry, target, stop" },
  { id: "synthesize", label: "SYNTHESIZE", detail: "Distill into ORACLE FX report" },
];

export const DISCLAIMERS = [
  "This is AI research for educational purposes only — not financial advice.",
  "All investments carry risk of loss. Do your own due diligence.",
  "Sports betting only where legal and 21+. Gamble responsibly.",
  "Forex trading involves substantial risk of loss.",
  "High-impact decisions require human-in-the-loop review.",
];

export const ORACLE_TEST_IDS = {
  modeTab: (id) => `oracle-mode-tab-${id}`,
  quickPick: (id, i) => `oracle-quickpick-${id}-${i}`,
  sampleQuery: (id, i) => `oracle-sample-${id}-${i}`,
  oandaPair: (sym) => `oracle-oanda-pair-${sym.replace('/', '-')}`,
  sendBtn: "oracle-send",
  input: "oracle-input",
  clear: "oracle-clear",
  assistantMsg: "oracle-assistant-msg",
  userMsg: "oracle-user-msg",
  thinking: "oracle-thinking",
};
